import { useCallback, useMemo, useState } from "react";
import { isEqual, noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { AnimalInfo, GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

// import { newSectionsData } from "../../../Assets/TestData/sectionsData";

import { LfSectionItem } from "./LocationFeedingSlices/lfStructureSlice";
import { CreatePanelLocation, CreatePanelLocationResponse, CreateTerminalLink, PANEL_CREATE_SECTION } from "./GQLCalls/createLfSectionCall";
import { GET_LF_SECTION_INFO, Location, SectionResponse, SectionsInfo } from "./GQLCalls/getLfStructureCall";
import { START_NEXT_FEEDING_BY_LOCATION, START_NEXT_FEEDING_BY_SYSTEM, StartNextFeedingByLocation, StartNextFeedingByLocationResponse, StartNextFeedingBySystemResponse } from "./GQLCalls/startNextFeedingCall";
import { FEED_LOCATION, FEED_LOCATION_MULTIPLE, FeedLocationMultiple, FeedLocationMultipleResponse, FeedLocationResponse } from "./GQLCalls/feedLocation";
import { normalizeAnimalCorrection } from "../AnimalDataCalls/useAnimal";
import { SimpleMutationResponse } from "../sharedTypes";
import { DO_EMPTY_FEED_LOCATION, DoEmptyFeedLocation, DoEmptyFeedLocationResponse } from "./GQLCalls/doEmptyFeed";
import { ConfigUpdateValue, DO_LOCATION_CALIBRATION, DoLocationCalibration, DoLocationCalibrationResponse, UPDATE_CONFIG_FROM_CALIBRATION_LOCATION, UpdateConfigFromCalibrationLocation, UpdateConfigFromCalibrationLocationResponse } from "./GQLCalls/calibrationCalls";
import { batchedMutation } from "../batchedMutation";
import { Def_MutationResponse } from "../UserCalls/GQLCalls/userCalls";
import { upsertAnimals } from "../AnimalDataCalls/AnimalDataSlices/animalsSlice";

interface LfSectionsHelperFunctions {
  createSectionLocations: (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[], newTerminalLinks?: CreateTerminalLink[]) => Promise<Def_MutationResponse>;
  startNextFeedingByLocation: (locationIds: number[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  startFeedingBySystem: (systemId: number) => void;
  feedLocation: (locationid: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
  filteredBySystemId: (domain: string, systemId: number) => SectionsInfo[];
  filteredBySystemIdSectionIndex: (domain: string, systemId: number, SectionIndex: number) => { index: number, name: string, pens: Location[] };
  getPensFromSectionIdPenIndex: (domain: string, systemId: number, SectionIndex: number, LocationIndex: number) => { index: number, name: string, animals: AnimalInfo[] };
  feedLocationMultiple: (locationIds: number[], overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>;
  doEmptyFeedLocation: (locationIds: number[], triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons | undefined, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  doLocationCalibration: (locationIds: number[], triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons | undefined, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
  updateConfigFromCalibrationLocation: (updateValues: ConfigUpdateValue[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal) => Promise<SimpleMutationResponse[]>
}

interface SectionInfoInput extends BasicQueryInfoInput {
  idIdentifier?: string;
}


export const useSection = (connection: Graph_QL.GQLCons): GqlHook_Returns<LfSectionItem, SectionInfoInput> & LfSectionsHelperFunctions => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const lfSectionsState = useAppSelector(({graphQlData:{domain}}) => domain.lfSections);
  const { fullState } = useMemo(() => {
    // console.log("State changed", ); TODO: State check
    const state = filterByConnection(lfSectionsState, Graph_QL.ToArr(connection)) as Record<string, LfSectionItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<LfSectionItem>)
  },[lfSectionsState]);

  const filteredBySystemId = (domain: string, systemId: number) => {
    let sections = fullState[domain] ? fullState[domain].data.filter(s => s.system_id === systemId) : []
    return sections;
  }

  const filteredBySystemIdSectionIndex = (domain: string, systemId: number, SectionIndex?: number) => {
    let sections = filteredBySystemId(domain, systemId)
    
    let SectionIndexed = sections.findIndex(s => s.section_id === SectionIndex);
    let SectionName = sections[SectionIndexed]?.name
    let SectionPens = sections[SectionIndexed]?.section_locations ?? [];
    return {index: SectionIndexed, name: SectionName, pens: SectionPens}
  }

  const getPensFromSectionIdPenIndex = (domain: string, systemId: number, SectionIndex?: number, LocationIndex?: number) => {
    let SectionLocations = filteredBySystemIdSectionIndex(domain, systemId, SectionIndex).pens;

    let locationIndexed = SectionLocations.findIndex(l => l.location_id === LocationIndex);
    let locationName = SectionLocations[locationIndexed]?.name;
    let locationAnimals = SectionLocations[locationIndexed]?.animals ?? [];
    return { index: locationIndexed, name: locationName, animals: locationAnimals }
  }

  const getLfSectionInfo = useCallback(async (input: SectionInfoInput, specific?: Graph_QL.GQLCons, ) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    const {idIdentifier, ...remainingInput} = input;
    const usedInput = remainingInput ? remainingInput : { fetchAmount: 500 };
    
    try {
      client.query<SectionResponse>({ query: GET_LF_SECTION_INFO, variables: { ...usedConnection, input: usedInput, idIdentifier, ifFeedCurveBaseInfoOnly: true, ifFeedCurveInfo: false, ifSpreading: false, ifSkipConsumption: false, ifSkipLastTenDaysConsumption: false }, fetchPolicy: "network-only" })// input.ids && input.ids?.length > 0 ? "network-only" : undefined}) // TODO CTO shouldn't always be network only
        .then((e) => {
          if (e && e.data) {
            let result_data:SectionsInfo[] = e.data.section.info.map((section) => ({...section, section_locations: section.section_locations.map((location) => ({...location, animals: location.animals.map(normalizeAnimalCorrection)})) }));
            dispatch({type: String(gqlActions.domain.lfSections.maintained), payload: {con: usedConnection, data: result_data}});
            const animalsInResult = result_data.flatMap((section) => section.section_locations.flatMap((location) => location.animals));
            if (animalsInResult.length) {
              dispatch(upsertAnimals(animalsInResult));
            }
            setCursorId(result_data.at(-1)?.section_id ?? -1)
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
        console.log(error);
      }
    },[])

  const createSectionLocations = async (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[], newTerminalLinks?: CreateTerminalLink[]) => {
    const errorResponse = {success: false, code: "400", message: "Failed to create section"}
    try {
      const result = await client.mutate<CreatePanelLocationResponse>({mutation: PANEL_CREATE_SECTION, variables: {domain, sectionName, panelSectionType, systemId, section: newSection, terminalLinks: newTerminalLinks}})
        if (result.data?.section.create.success) {
          getLfSectionInfo({ids: result.data.section.create.updatedValues, idIdentifier: "section_id"}, connection)
        }
      if (result.data?.section.create) {
        return result.data?.section.create;
      }
      return errorResponse;
    } catch (error) {
      return errorResponse; 
    }
  }

  const startNextFeedingByLocation = useCallback(
    async (locationIds: number[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]>  => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const {domain} = usedConnection as {domain: string};
      return await batchedMutation<StartNextFeedingByLocationResponse, StartNextFeedingByLocation>(
        client,
        {
          mutation: START_NEXT_FEEDING_BY_LOCATION,
          variables: { domain, locationids: locationIds },
          responsePath: (data) => data?.location.startNextFeeding,
          ids: locationIds,
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  /**
   * @deprecated needs to return which systems succeded
   */
  const startFeedingBySystem = async (systemId: number) => {
    try {
      client.mutate<StartNextFeedingBySystemResponse>({mutation: START_NEXT_FEEDING_BY_SYSTEM, variables: {...connection.connection, feedCurveId: 0, systemId}})
        .then((result) => {
          console.log(result);
        })
    } catch (error) {
      console.log(error);
    }
  }

    const feedLocation = useCallback(async (locationId: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<FeedLocationResponse>({mutation: FEED_LOCATION, variables: {...usedConnection, locationid: locationId, overwriteFeedcurve, amount}})
        .then((e) => {
          const feedLocation = e.data?.location.feed;
          if (feedLocation) {
            return feedLocation.success? true:false;
          }
          return false;
        }).catch((error) => {return false})
      } catch (error) {console.log(error); return false}
    },[client, connection.connection])
    
  const feedLocationMultiple = useCallback(
    async (locationIds: number[], overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]>  => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const {domain} = usedConnection as {domain: string};
      return await batchedMutation<FeedLocationMultipleResponse, FeedLocationMultiple>(
        client,
        {
          mutation: FEED_LOCATION_MULTIPLE,
          variables: { domain, locationIds, overwriteFeedcurve, amount },
          responsePath: (data) => data?.location.feedMultiple,
          ids: locationIds,
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const doEmptyFeedLocation = useCallback(
    async (locationIds: number[], triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const {domain} = usedConnection as {domain: string};
      return await batchedMutation<DoEmptyFeedLocationResponse, DoEmptyFeedLocation>(
        client,
        {
          mutation: DO_EMPTY_FEED_LOCATION,
          variables: { domain, locationIds, triggerAmount, pauseTime, amount},
          responsePath: (data) => data?.location.doEmptyFeed,
          ids: locationIds
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const doLocationCalibration = useCallback(
    async (locationIds: number[], triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const {domain} = usedConnection as {domain: string};
      return await batchedMutation<DoLocationCalibrationResponse, DoLocationCalibration>(
        client,
        {
          mutation: DO_LOCATION_CALIBRATION,
          variables: { domain, locationIds, triggerAmount, pauseTime, calibrationAmount },
          responsePath: (data) => data?.location.doCalibration,
          ids: locationIds
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  const updateConfigFromCalibrationLocation = useCallback(
    async (updateValues: ConfigUpdateValue[], specific?: Graph_QL.GQLCons, abortSignal?: AbortSignal): Promise<SimpleMutationResponse[]> => {
      const usedConnection = specific ? specific.connection : connection.connection;
      const {domain} = usedConnection as {domain: string};
      return await batchedMutation<UpdateConfigFromCalibrationLocationResponse, UpdateConfigFromCalibrationLocation>(
        client,
        {
          mutation: UPDATE_CONFIG_FROM_CALIBRATION_LOCATION,
          variables: { domain, updateValues },
          responsePath: (data) => data?.location.updateConfigFromCalibration,
          ids: updateValues.map(e => e.id)
        },
        abortSignal
      );
    },
    [client, connection.connection]
  );

  return {call: getLfSectionInfo, fetchMore: noop, deleteEntries: noop, data: fullState, filteredBySystemId, filteredBySystemIdSectionIndex, getPensFromSectionIdPenIndex, maintainEntries: noop, createSectionLocations, startNextFeedingByLocation, startFeedingBySystem, feedLocation, feedLocationMultiple, doEmptyFeedLocation, doLocationCalibration, updateConfigFromCalibrationLocation}
} 

